import { AddIcon } from '@chakra-ui/icons'
import {
  Box,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react'
import { useFirebase } from '@chem-po/firebase-web'
import { useApp, useAuth } from '@hb/react'
import {
  CheckboxField,
  FieldMap,
  FieldMapValue,
  FieldTypes,
  FileField,
  OnUploadProgress,
  TextField,
} from '@hb/shared'
import { FORM_ERROR } from 'final-form'
import { FC, useCallback, useState } from 'react'
import { processFieldMapData } from '../../../backend/db/utils/processData'
import { ActionButton } from '../../Buttons/ActionButton'
import { AddItemProps } from '../../EditCollection/EditCollection'
import { SimpleForm } from '../../forms/FinalForm/SimpleForm'

const nameField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Template Name',
}

const isExternalPdfField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  inputType: 'switch',
  yesText: 'External PDF',
  noText: 'Custom Template',
  placeholder: 'External PDF',
}

const externalPdfField: FileField = {
  type: FieldTypes.FILE,
  placeholder: 'External PDF',
  fileType: 'pdf',
  name: 'externalPdf',
  condition: v => !!v?.isExternalPdf,
}

const field: FieldMap = {
  name: 'Template',
  children: {
    name: nameField,
    isExternalPdf: isExternalPdfField,
    externalPdf: externalPdfField,
  },
}

export const AddConsentForm: FC<AddItemProps> = ({ itemName, loading, onAdd }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { appName } = useApp()
  const { admin } = useAuth()

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const { auth } = useFirebase()
  const handleSubmit = useCallback(
    async (v: FieldMapValue, onUploadProgress: OnUploadProgress) => {
      const uid = auth.currentUser?.uid
      if (!uid) {
        return { [FORM_ERROR]: 'Not logged in' }
      }
      const now = new Date().toISOString()
      try {
        const processed = await processFieldMapData(
          appName,
          admin,
          uid,
          `temp/templates/${now}`,
          field,
          v,
          null,
          onUploadProgress,
        )
        await onAdd(processed)
        setIsOpen(false)
      } catch (e: any) {
        console.error(e)
        return { [FORM_ERROR]: e.message }
      }
    },
    [onAdd, auth, appName, admin],
  )

  return (
    <Box p={2}>
      <Popover
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={handleClose}
        closeOnBlur={false}
        strategy="fixed"
        isLazy
        placement="right-start">
        <PopoverTrigger>
          <ActionButton
            borderRadius="full"
            gap={1.5}
            isLoading={loading}
            size="sm"
            variant="outline">
            <AddIcon w={3} h={3} />
            <Text>{`Add ${itemName}`}</Text>
          </ActionButton>
        </PopoverTrigger>
        <PopoverContent bg="gray.50" w="350px" borderRadius={10}>
          <PopoverArrow bg="gray.50" />
          <PopoverBody p={0}>
            <Flex w="100%" flexFlow="column">
              <Flex
                bg="white"
                borderTopRadius={10}
                px={2}
                borderBottom="1px solid #cdcdcd"
                w="100%"
                py={1.5}>
                <Text px={1} fontSize="sm" fontFamily="Hero-New" color="gray.500">
                  New Template
                </Text>
              </Flex>
              <SimpleForm
                boxProps={{ bg: 'transparent', boxShadow: 'none', p: 0 }}
                field={field}
                onSubmit={handleSubmit}
              />
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  )
}
